import { useState } from 'react'
import { useNavigate, useParams } from '@tanstack/react-router'
import { ArrowLeft, Pencil, RefreshCw } from 'lucide-react'
import { toast } from 'sonner'
import { Button, Chip, Input, PartnerPicker, Select, Textarea } from '@/components/ui'
import type { PickerValue } from '@/components/ui/ProductPicker'
import { useHasAnyPermission } from '@/shell/module-system'
import { EDIT_ROLES } from '../utils/permissions'
import { odooUrl } from '@/lib/utils/odoo-url'
import {
  useBlockSampleOrder,
  useCancelSampleOrder,
  useCloseOutSampleOrder,
  useConfirmSampleOrder,
  usePatchSampleOrder,
  useSampleOrderDetail,
  useSampleOrderReps,
  useTransitionSampleOrder,
} from '../api/queries'
import type { BlockedReason, PipelineType, Stage } from '../api/types'
import {
  BLOCKED_REASON_LABELS,
  BLOCKED_REASONS,
  PIPELINE_TYPE_LABELS,
  PIPELINE_TYPES,
  STAGE_LABELS,
  STAGE_VARIANT,
  allowedTargetsFrom,
} from '../utils/stage-meta'
import { StageHistoryTimeline } from '../components/StageHistoryTimeline'
import { ShipmentsTable } from '../components/ShipmentsTable'

const FULFILLMENT_LABELS: Record<string, string> = {
  pending: 'Pending',
  partial: 'Partly shipped',
  shipped: 'Shipped',
  written_off: 'Written off',
}

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
}

export function SampleOrderDetailPage() {
  const params = useParams({ strict: false }) as { orderId?: string }
  const orderId = params.orderId ?? ''
  const navigate = useNavigate()
  const detail = useSampleOrderDetail(orderId)
  const transition = useTransitionSampleOrder()
  const block = useBlockSampleOrder()
  const confirm = useConfirmSampleOrder()
  const cancel = useCancelSampleOrder()
  const closeOut = useCloseOutSampleOrder()
  const patch = usePatchSampleOrder()
  const repsQuery = useSampleOrderReps()
  const reps = repsQuery.data?.reps ?? []

  const [editMode, setEditMode] = useState(false)
  const [ownerDraft, setOwnerDraft] = useState<string | null>(null)
  const [companyDraft, setCompanyDraft] = useState<string | null>(null)
  const [emailDraft, setEmailDraft] = useState<string | null>(null)
  const [phoneDraft, setPhoneDraft] = useState<string | null>(null)
  const [contactNameDraft, setContactNameDraft] = useState<string | null>(null)
  const [shipToDraft, setShipToDraft] = useState<string | null>(null)
  const [notesDraft, setNotesDraft] = useState<string | null>(null)
  const [blockReason, setBlockReason] = useState<BlockedReason>('out_of_stock')
  const [blockDetail, setBlockDetail] = useState('')
  const [showBlockForm, setShowBlockForm] = useState(false)

  function clearDrafts() {
    setOwnerDraft(null)
    setCompanyDraft(null)
    setEmailDraft(null)
    setPhoneDraft(null)
    setContactNameDraft(null)
    setShipToDraft(null)
    setNotesDraft(null)
  }

  function stopEditing() {
    clearDrafts()
    setEditMode(false)
  }

  const canEdit = useHasAnyPermission(EDIT_ROLES)

  const order = detail.data

  if (detail.isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <p className="text-sm text-tertiary">Loading…</p>
      </div>
    )
  }

  if (!order) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-4">
        <p className="text-sm text-tertiary">Sample order not found.</p>
        <Button variant="secondary" size="sm" onClick={() => navigate({ to: '/sample-orders' })}>
          Back to board
        </Button>
      </div>
    )
  }

  const allowedTargets = allowedTargetsFrom(order.status).filter((t) => t !== 'blocked')
  const customer = order.customer_name ?? order.prospect_name ?? '—'
  const isProspectPath = order.customer_id == null
  const ownerValue = ownerDraft ?? order.assigned_to ?? ''
  const companyValue = companyDraft ?? order.prospect_company ?? ''
  const emailValue = emailDraft ?? order.prospect_email ?? ''
  const phoneValue = phoneDraft ?? order.prospect_phone ?? ''
  const contactNameValue = contactNameDraft ?? order.contact_name ?? ''
  const shipToValue = shipToDraft ?? order.ship_to_address ?? ''
  const notesValue = notesDraft ?? order.notes ?? ''
  const ownerDisplayName =
    reps.find((r) => r.email === order.assigned_to)?.name ?? order.assigned_to ?? '—'

  function handleTransition(target: Stage) {
    transition.mutate(
      { orderId: order!.id, targetStage: target },
      {
        onSuccess: () => toast.success(`Moved to ${STAGE_LABELS[target]}`),
        onError: (err) =>
          toast.error(err instanceof Error ? err.message : 'transition failed', {
            duration: 5000,
          }),
      },
    )
  }

  function submitBlock() {
    block.mutate(
      {
        orderId: order!.id,
        blockedReason: blockReason,
        blockedReasonDetail: blockDetail.trim() || undefined,
      },
      {
        onSuccess: () => {
          toast.success('Marked blocked')
          setShowBlockForm(false)
          setBlockDetail('')
        },
        onError: (err) => toast.error(err instanceof Error ? err.message : 'block failed'),
      },
    )
  }

  function handleConfirm() {
    confirm.mutate(order!.id, {
      onSuccess: (o) => toast.success(`Draft Odoo order ${o.odoo_so_name ?? ''} created`),
      onError: (err) =>
        toast.error(err instanceof Error ? err.message : 'confirm failed', { duration: 5000 }),
    })
  }

  function handleCancel() {
    cancel.mutate(
      { orderId: order!.id },
      {
        onSuccess: () => toast.success('Order cancelled'),
        onError: (err) => toast.error(err instanceof Error ? err.message : 'cancel failed'),
      },
    )
  }

  function handleCloseOut() {
    if (
      !window.confirm(
        'Close out the remainder? This cancels the outstanding backorder in Odoo, ' +
          'writes off the unshipped line(s), and marks the order Shipped. This cannot be undone.',
      )
    ) {
      return
    }
    closeOut.mutate(
      { orderId: order!.id },
      {
        onSuccess: () => toast.success('Remainder closed out — order marked Shipped'),
        onError: (err) => toast.error(err instanceof Error ? err.message : 'close-out failed'),
      },
    )
  }

  /** Shared PATCH helper for every header field below. `onSuccess` clears
   * the caller's draft state so the field reverts to reading from `order`
   * (which react-query will have refreshed). */
  function saveField(
    field: string,
    value: unknown,
    opts?: { successMessage?: string; onDone?: () => void },
  ) {
    patch.mutate(
      { orderId: order!.id, edits: { [field]: value } },
      {
        onSuccess: () => {
          toast.success(opts?.successMessage ?? 'Updated')
          opts?.onDone?.()
        },
        onError: (err) => toast.error(err instanceof Error ? err.message : 'Update failed'),
      },
    )
  }

  function saveOwner() {
    saveField('assigned_to', ownerValue.trim() || null, {
      successMessage: 'Owner updated',
      onDone: () => setOwnerDraft(null),
    })
  }

  function savePipelineType(value: PipelineType) {
    saveField('pipeline_type', value, { successMessage: 'Pipeline type updated' })
  }

  function saveNeedBy(value: string) {
    saveField('need_by_date', value || null, { successMessage: 'Need-by updated' })
  }

  function saveContactPicked(picked: PickerValue) {
    patch.mutate(
      {
        orderId: order!.id,
        edits: {
          contact_id: picked ? picked[0] : null,
          contact_name: picked ? picked[1] : null,
        },
      },
      {
        onSuccess: () => toast.success('Contact updated'),
        onError: (err) => toast.error(err instanceof Error ? err.message : 'Update failed'),
      },
    )
  }

  function saveContactName() {
    saveField('contact_name', (contactNameDraft ?? '').trim() || null, {
      successMessage: 'Contact updated',
      onDone: () => setContactNameDraft(null),
    })
  }

  function saveCompany() {
    saveField('prospect_company', (companyDraft ?? '').trim() || null, {
      successMessage: 'Company updated',
      onDone: () => setCompanyDraft(null),
    })
  }

  function saveEmail() {
    saveField('prospect_email', (emailDraft ?? '').trim() || null, {
      successMessage: 'Email updated',
      onDone: () => setEmailDraft(null),
    })
  }

  function savePhone() {
    saveField('prospect_phone', (phoneDraft ?? '').trim() || null, {
      successMessage: 'Phone updated',
      onDone: () => setPhoneDraft(null),
    })
  }

  function saveShipTo() {
    saveField('ship_to_address', (shipToDraft ?? '').trim() || null, {
      successMessage: 'Ship-to updated',
      onDone: () => setShipToDraft(null),
    })
  }

  function saveNotes() {
    saveField('notes', (notesDraft ?? '').trim() || null, {
      successMessage: 'Notes updated',
      onDone: () => setNotesDraft(null),
    })
  }

  return (
    <div className="flex h-full min-h-0 w-full flex-col bg-canvas">
      <header className="flex flex-shrink-0 items-center gap-4 border-b border-border-subtle bg-surface px-6 py-3">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate({ to: '/sample-orders' })}
          aria-label="Back to board"
        >
          <ArrowLeft size={16} />
        </Button>
        <div className="flex flex-col">
          <h1 className="text-role-page-title m-0">{customer}</h1>
          <p className="text-role-page-subtitle m-0">{order.order_number}</p>
        </div>
        <div className="ml-auto flex items-center gap-3">
          <Chip size="xs" variant={STAGE_VARIANT[order.status]} dot>
            {STAGE_LABELS[order.status]}
          </Chip>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => detail.refetch()}
            disabled={detail.isFetching}
          >
            <RefreshCw size={14} />
          </Button>
        </div>
      </header>

      <div className="flex flex-1 min-h-0 gap-0">
        <div className="flex flex-1 min-h-0 flex-col gap-6 overflow-y-auto px-6 py-6">
          <section className="rounded-lg border border-border-subtle bg-surface p-5">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-role-section-label m-0">Order</h2>
              {canEdit ? (
                editMode ? (
                  <Button variant="secondary" size="sm" onClick={stopEditing}>
                    Done
                  </Button>
                ) : (
                  <Button variant="ghost" size="sm" onClick={() => setEditMode(true)}>
                    <Pencil size={14} className="mr-1.5" /> Edit
                  </Button>
                )
              ) : null}
            </div>
            <dl className="grid grid-cols-2 gap-x-8 gap-y-3 text-sm">
              <Field label="Customer / prospect" value={customer} primary />

              {isProspectPath && canEdit && editMode ? (
                <EditableTextField
                  label="Prospect company"
                  value={companyValue}
                  draft={companyDraft}
                  onChange={setCompanyDraft}
                  onSave={saveCompany}
                  originalValue={order.prospect_company ?? ''}
                  saving={patch.isPending}
                />
              ) : isProspectPath && order.prospect_company ? (
                <Field label="Prospect company" value={order.prospect_company} />
              ) : null}

              {isProspectPath && canEdit && editMode ? (
                <EditableTextField
                  label="Prospect email"
                  type="email"
                  value={emailValue}
                  draft={emailDraft}
                  onChange={setEmailDraft}
                  onSave={saveEmail}
                  originalValue={order.prospect_email ?? ''}
                  saving={patch.isPending}
                />
              ) : isProspectPath && order.prospect_email ? (
                <Field label="Prospect email" value={order.prospect_email} />
              ) : null}

              {isProspectPath && canEdit && editMode ? (
                <EditableTextField
                  label="Prospect phone"
                  type="tel"
                  value={phoneValue}
                  draft={phoneDraft}
                  onChange={setPhoneDraft}
                  onSave={savePhone}
                  originalValue={order.prospect_phone ?? ''}
                  saving={patch.isPending}
                />
              ) : isProspectPath && order.prospect_phone ? (
                <Field label="Prospect phone" value={order.prospect_phone} />
              ) : null}

              {canEdit && editMode && !isProspectPath ? (
                <div>
                  <dt className="text-role-metadata mb-0.5">Contact</dt>
                  <dd className="m-0">
                    <PartnerPicker
                      value={
                        order.contact_id != null && order.contact_name
                          ? [order.contact_id, order.contact_name]
                          : null
                      }
                      onChange={saveContactPicked}
                      kind="customer_contact"
                      parentId={order.customer_id}
                      placeholder="Pick a contact at this company…"
                    />
                  </dd>
                </div>
              ) : canEdit && editMode ? (
                <EditableTextField
                  label="Contact"
                  value={contactNameValue}
                  draft={contactNameDraft}
                  onChange={setContactNameDraft}
                  onSave={saveContactName}
                  originalValue={order.contact_name ?? ''}
                  saving={patch.isPending}
                />
              ) : (
                <Field label="Contact" value={order.contact_name ?? '—'} />
              )}

              {canEdit && editMode ? (
                <div>
                  <dt className="text-role-metadata mb-0.5">Owner</dt>
                  <dd className="m-0 flex items-center gap-2">
                    <Select
                      value={ownerValue}
                      disabled={repsQuery.isLoading}
                      onChange={(e) => setOwnerDraft(e.target.value)}
                      aria-label="Owner"
                    >
                      <option value="">{repsQuery.isLoading ? 'Loading…' : 'Unassigned'}</option>
                      {reps.map((rep) => (
                        <option key={rep.email} value={rep.email}>
                          {rep.name}
                        </option>
                      ))}
                    </Select>
                    {ownerDraft !== null && ownerDraft !== (order.assigned_to ?? '') ? (
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={saveOwner}
                        loading={patch.isPending}
                      >
                        Save
                      </Button>
                    ) : null}
                  </dd>
                </div>
              ) : (
                <Field label="Owner" value={ownerDisplayName} />
              )}

              {canEdit && editMode ? (
                <div>
                  <dt className="text-role-metadata mb-0.5">Pipeline type</dt>
                  <dd className="m-0">
                    <Select
                      value={order.pipeline_type}
                      disabled={patch.isPending}
                      onChange={(e) => savePipelineType(e.target.value as PipelineType)}
                      aria-label="Pipeline type"
                    >
                      {PIPELINE_TYPES.map((pt) => (
                        <option key={pt} value={pt}>
                          {PIPELINE_TYPE_LABELS[pt]}
                        </option>
                      ))}
                    </Select>
                  </dd>
                </div>
              ) : (
                <Field label="Pipeline type" value={order.pipeline_type_label} />
              )}

              <Field label="Requested by" value={order.requested_by ?? '—'} />

              {canEdit && editMode ? (
                <div>
                  <dt className="text-role-metadata mb-0.5">Need by</dt>
                  <dd className="m-0">
                    <Input
                      type="date"
                      value={order.need_by_date ?? ''}
                      onChange={(e) => saveNeedBy(e.target.value)}
                      aria-label="Need by"
                    />
                  </dd>
                </div>
              ) : (
                <Field label="Need by" value={formatDate(order.need_by_date)} />
              )}

              <div>
                <dt className="text-role-metadata mb-0.5">Sales order</dt>
                <dd className="m-0 text-role-data-secondary">
                  {order.odoo_so_id && order.odoo_so_name ? (
                    <a
                      href={odooUrl('sale.order', order.odoo_so_id)}
                      target="_blank"
                      rel="noreferrer"
                      className="text-link hover:underline"
                    >
                      {order.odoo_so_name}
                    </a>
                  ) : (
                    <span className="text-tertiary">— not yet created —</span>
                  )}
                </dd>
              </div>
              <Field label="Requested" value={formatDate(order.created_at)} />
            </dl>
            {order.status === 'blocked' && order.blocked_reason_label ? (
              <div className="mt-4 rounded bg-surface-sunk p-3 text-sm">
                <span className="text-role-metadata">Blocked — {order.blocked_reason_label}</span>
                {order.blocked_reason_detail ? (
                  <p className="m-0 mt-1 text-secondary">{order.blocked_reason_detail}</p>
                ) : null}
              </div>
            ) : null}

            {canEdit && editMode ? (
              <div className="mt-4">
                <EditableTextField
                  label="Ship-to"
                  value={shipToValue}
                  draft={shipToDraft}
                  onChange={setShipToDraft}
                  onSave={saveShipTo}
                  originalValue={order.ship_to_address ?? ''}
                  saving={patch.isPending}
                  multiline
                />
              </div>
            ) : order.ship_to_address ? (
              <div className="mt-4">
                <div className="text-role-metadata mb-0.5">Ship-to</div>
                <div className="whitespace-pre-line text-sm text-secondary">
                  {order.ship_to_address}
                </div>
              </div>
            ) : null}

            {canEdit && editMode ? (
              <div className="mt-4">
                <EditableTextField
                  label="Notes"
                  value={notesValue}
                  draft={notesDraft}
                  onChange={setNotesDraft}
                  onSave={saveNotes}
                  originalValue={order.notes ?? ''}
                  saving={patch.isPending}
                  multiline
                />
              </div>
            ) : order.notes ? (
              <div className="mt-4 rounded bg-surface-sunk p-3 text-sm text-secondary">
                {order.notes}
              </div>
            ) : null}
          </section>

          {order.lines && order.lines.length > 0 ? (
            <section className="rounded-lg border border-border-subtle bg-surface p-5">
              <h2 className="text-role-section-label mb-4">Requested products</h2>
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border-subtle">
                    <th className="text-role-table-header py-2 text-left">Product</th>
                    <th className="text-role-table-header py-2 text-right">Qty</th>
                    <th className="text-role-table-header py-2 text-left">UOM</th>
                    <th className="text-role-table-header py-2 text-right">Shipped</th>
                    <th className="text-role-table-header py-2 text-left">Fulfillment</th>
                  </tr>
                </thead>
                <tbody>
                  {order.lines.map((line) => (
                    <tr key={line.id} className="border-b border-border-subtle last:border-0">
                      <td className="text-role-table-cell py-2">
                        {line.matched_product_name ?? line.parsed_description ?? '—'}
                      </td>
                      <td className="text-role-table-cell py-2 text-right">
                        {line.final_qty != null ? line.final_qty : '—'}
                      </td>
                      <td className="text-role-table-cell py-2">{line.uom ?? '—'}</td>
                      <td className="text-role-table-cell py-2 text-right">{line.shipped_qty}</td>
                      <td className="text-role-table-cell py-2">
                        <Chip
                          size="xs"
                          variant={
                            line.fulfillment_status === 'shipped'
                              ? 'success'
                              : line.fulfillment_status === 'partial'
                                ? 'warning'
                                : line.fulfillment_status === 'written_off'
                                  ? 'neutral'
                                  : 'neutral'
                          }
                        >
                          {FULFILLMENT_LABELS[line.fulfillment_status] ?? line.fulfillment_status}
                        </Chip>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          ) : null}

          <section className="rounded-lg border border-border-subtle bg-surface p-5">
            <h2 className="text-role-section-label mb-4">Delivery &amp; tracking</h2>
            {order.shipments && order.shipments.length > 0 ? (
              <>
                <ShipmentsTable shipments={order.shipments} />
                {order.delivered_at ? (
                  <p className="mt-3 text-sm text-secondary">
                    All deliveries delivered {formatDate(order.delivered_at)}.
                  </p>
                ) : null}
              </>
            ) : (
              <dl className="grid grid-cols-2 gap-x-8 gap-y-3 text-sm">
                <Field
                  label="Odoo delivery"
                  value={order.odoo_picking_name ?? '— not linked —'}
                  primary={!!order.odoo_picking_name}
                />
                <Field label="Delivery state" value={order.odoo_picking_state ?? '—'} />
                <Field
                  label="Tracking #"
                  value={
                    order.tracking_number ? (
                      order.tracking_link ? (
                        <a
                          href={order.tracking_link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-link hover:underline"
                        >
                          {order.tracking_number}
                        </a>
                      ) : (
                        order.tracking_number
                      )
                    ) : (
                      '—'
                    )
                  }
                />
                <Field label="Carrier" value={order.carrier_name ?? '—'} />
                <Field label="Lot #" value={order.lot_number ?? '—'} />
                <Field label="Expiration" value={formatDate(order.expiration_date)} />
                <Field label="Shipped" value={formatDate(order.shipped_at)} />
                <Field label="Delivered" value={formatDate(order.delivered_at)} />
              </dl>
            )}
            {!order.odoo_so_id && canEdit ? (
              <div className="mt-4">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={handleConfirm}
                  loading={confirm.isPending}
                >
                  Confirm — create draft Odoo order
                </Button>
              </div>
            ) : null}
            {order.status === 'partially_shipped' && canEdit ? (
              <div className="mt-4">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleCloseOut}
                  loading={closeOut.isPending}
                >
                  Close out remainder…
                </Button>
                <p className="mt-1 text-xs text-secondary">
                  Cancels the outstanding backorder in Odoo, writes off the unshipped line(s), and
                  marks the order Shipped.
                </p>
              </div>
            ) : null}
          </section>
        </div>

        <aside className="w-72 flex-shrink-0 overflow-y-auto border-l border-border-subtle bg-surface p-5">
          {canEdit && allowedTargets.length > 0 ? (
            <section className="mb-6">
              <h3 className="text-role-section-label mb-3">Move pipeline</h3>
              <div className="flex flex-col gap-1.5">
                {allowedTargets.map((target) => (
                  <Button
                    key={target}
                    variant="secondary"
                    size="sm"
                    className="justify-start"
                    onClick={() => handleTransition(target)}
                    disabled={transition.isPending}
                  >
                    <Chip size="xs" variant={STAGE_VARIANT[target]} dot className="mr-2" />
                    {STAGE_LABELS[target]}
                  </Button>
                ))}
              </div>
            </section>
          ) : null}

          {canEdit &&
          order.status !== 'blocked' &&
          order.status !== 'shipped' &&
          order.status !== 'cancelled' ? (
            <section className="mb-6">
              <h3 className="text-role-section-label mb-3">Block</h3>
              {!showBlockForm ? (
                <Button variant="secondary" size="sm" onClick={() => setShowBlockForm(true)}>
                  Mark blocked…
                </Button>
              ) : (
                <div className="flex flex-col gap-2">
                  <Select
                    value={blockReason}
                    onChange={(e) => setBlockReason(e.target.value as BlockedReason)}
                  >
                    {BLOCKED_REASONS.map((r) => (
                      <option key={r} value={r}>
                        {BLOCKED_REASON_LABELS[r]}
                      </option>
                    ))}
                  </Select>
                  <Input
                    value={blockDetail}
                    onChange={(e) => setBlockDetail(e.target.value)}
                    placeholder="Optional detail"
                  />
                  <div className="flex gap-2">
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={submitBlock}
                      loading={block.isPending}
                    >
                      Save
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => setShowBlockForm(false)}>
                      Cancel
                    </Button>
                  </div>
                </div>
              )}
            </section>
          ) : null}

          {canEdit && order.status !== 'shipped' && order.status !== 'cancelled' ? (
            <section className="mb-6">
              <Button variant="ghost" size="sm" onClick={handleCancel} loading={cancel.isPending}>
                Cancel order
              </Button>
            </section>
          ) : null}

          <section>
            <h3 className="text-role-section-label mb-3">History</h3>
            <StageHistoryTimeline entries={order.stage_history ?? []} />
          </section>
        </aside>
      </div>
    </div>
  )
}

function Field({
  label,
  value,
  primary,
}: {
  label: string
  value: React.ReactNode
  primary?: boolean
}) {
  return (
    <div>
      <dt className="text-role-metadata mb-0.5">{label}</dt>
      <dd className={`m-0 ${primary ? 'text-role-data-primary' : 'text-role-data-secondary'}`}>
        {value}
      </dd>
    </div>
  )
}

/**
 * Draft-state + appears-when-dirty Save button, matching the existing
 * Owner field's edit UX exactly — one local helper for the ~6 free-text
 * header fields where char-by-char PATCHing would be wrong (need a
 * deliberate commit moment), rather than duplicating the same
 * state+button wiring six times inline.
 */
function EditableTextField({
  label,
  value,
  draft,
  onChange,
  onSave,
  originalValue,
  saving,
  type = 'text',
  multiline = false,
}: {
  label: string
  value: string
  draft: string | null
  onChange: (v: string) => void
  onSave: () => void
  originalValue: string
  saving: boolean
  type?: string
  multiline?: boolean
}) {
  const dirty = draft !== null && draft !== originalValue
  return (
    <div>
      <dt className="text-role-metadata mb-0.5">{label}</dt>
      <dd className="m-0 flex items-start gap-2">
        {multiline ? (
          <Textarea value={value} onChange={(e) => onChange(e.target.value)} rows={2} />
        ) : (
          <Input type={type} value={value} onChange={(e) => onChange(e.target.value)} />
        )}
        {dirty ? (
          <Button variant="secondary" size="sm" onClick={onSave} loading={saving}>
            Save
          </Button>
        ) : null}
      </dd>
    </div>
  )
}
